import { api } from "@/lib/api";
import {
  TourDto,
  tourDtoListSchema,
  TourDtoSchema,
  tourFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getToursQueryOptions = queryOptions({
  queryKey: ["tours"],
  queryFn: async () => {
    const result: Array<TourDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache("/api/tours", tourDtoListSchema, {
        query: {
          page: page.toString(),
        },
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(tourFromDto);
  },
});

export function useGetTours<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({
    ...getToursQueryOptions,
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getToursError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getTourQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["tours", id],
    queryFn: () =>
      api
        .get("/api/tours/$id", TourDtoSchema, {
          pathReplace: { $id: id },
        })
        .then(tourFromDto),
  });
}

export function useGetTour<TERROR extends Error>(
  id: string,
  opts?: { onError: (error: TERROR) => void },
) {
  const { toast } = useToast();
  const q = useQuery(getTourQueryOptions(id));
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getTourDetailError",
      variant: "destructive",
    });
    opts?.onError(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
